import React, { forwardRef, Ref } from 'react';
import styled, { css, StyledComponent } from 'styled-components';
import { Colors, Checkbox, Theme } from '@inplayer-org/inplayer-ui';

// utils
import truncateTextWithTooltip from 'utils/truncateTextWithTooltip';

const sharedBoxStyle = () => css`
  box-shadow: 0 0.2rem 0.5rem ${Colors.gray};
  transform: scale(1.05);
  /* show truncate text tooltip */
  z-index: 1;
`;

interface ItemContainerProps {
  isChecked?: boolean;
  isDraggingMultiple: boolean;
}

const ItemContainer: StyledComponent<'div', Theme, ItemContainerProps> = styled.div`
  display: flex;
  width: 100%;
  height: 4rem;
  margin: 1rem 0;
  border: 1px solid ${Colors.fontLightGray};
  border-radius: 0.3rem;
  align-items: center;
  background-color: ${Colors.white};
  position: relative;
  transition: transform 0.3s ease-in-out 0s;

  ${({ isChecked }: ItemContainerProps) =>
    isChecked &&
    css`
      ${sharedBoxStyle}
    `}

  &:hover {
    ${sharedBoxStyle}
  }
  ${({ isDraggingMultiple }) =>
    isDraggingMultiple &&
    css`
      border: 2px solid ${Colors.fontLightGray};
      box-shadow: 2px 2px 1px ${Colors.fontGray};
    `};
`;

const Image = styled.img`
  width: 30%;
  height: 100%;
`;

const Title = styled.span`
  color: ${Colors.fontGray};
  font-size: ${({ theme }) => theme.font.sizes.small};
  font-weight: bold;
  margin: 0 0.2rem;
  width: 100%;
  text-align: center;
  word-break: break-word;

  /* Style for truncate title tooltip */
  > div > div {
    width: fit-content;
  }
`;

const CheckboxWrapper = styled.div<{ isDragged: boolean }>`
  position: absolute;
  top: 0.2rem;
  left: 0.2rem;
  ${({ isDragged }) => isDragged && 'display: none'};
`;

const SelectionCount = styled.div`
  right: -10px;
  top: -10px;
  color: white;
  background: ${Colors.darkBlue};
  border-radius: 50%;
  height: 2rem;
  width: 2rem;
  line-height: 2rem;
  position: absolute;
  text-align: center;
  font-size: ${({ theme }) => theme.font.sizes.small};
`;

interface OwnProps {
  image: string;
  title: string;
  id: number;
  checkedAssets: any;
  toggleCheckbox: (id: number) => any;
  isDraggingMultiple: boolean;
  totalItems: number;
  isDragged: boolean;
  isFirstItem: boolean;
  assetStatus: string;
}

type Props = OwnProps;

const AssetItem = forwardRef(
  (
    {
      image,
      title,
      id,
      checkedAssets,
      toggleCheckbox,
      isDraggingMultiple,
      totalItems,
      isDragged,
      isFirstItem,
      assetStatus,
      ...rest
    }: Props,
    ref: Ref<HTMLDivElement>
  ) => {
    const tooltipPlacement = isFirstItem ? 'bottom' : 'top';

    return (
      <ItemContainer ref={ref} isChecked={checkedAssets[id]} isDraggingMultiple={isDraggingMultiple} {...rest}>
        <CheckboxWrapper isDragged={isDragged}>
          <Checkbox
            tag={`checbox_select_asset_${assetStatus}_${id}`}
            label=""
            id={id.toString()}
            checked={!!checkedAssets[id]}
            onChange={toggleCheckbox(id)}
          />
        </CheckboxWrapper>
        <Image alt="preview-img" src={image} />
        <Title>{truncateTextWithTooltip({ text: title, placement: tooltipPlacement })}</Title>
        {isDraggingMultiple && <SelectionCount>{totalItems}</SelectionCount>}
      </ItemContainer>
    );
  }
);

export default AssetItem;
